import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { TreeOCRField } from "@/types/fieldMapping.types";
import { RootState } from "..";

export type FieldNodeType = "section" | "table" | "field";

export interface FieldNode {
  id: string;
  parentId?: string;
  nodeType: FieldNodeType;
  label: string;
  suggestedLabel?: string;
  rawFieldName?: string;
  sampleValue?: string;
  subtitle?: string;
  confidence?: number;
  tableName?: string;
  isSelected: boolean;
  isUniqueIdentifier?: boolean;
  childIds: string[];
  isUserAdded?: boolean;
  metaData?: unknown;
  apiResponseKey?: string; // API response key for newly added fields
}

export interface FieldMappingState {
  rootIds: string[];
  nodesById: Record<string, FieldNode>;
  uniqueFieldId: string | null;
  version: number;
}

const initialState: FieldMappingState = {
  rootIds: [],
  nodesById: {},
  uniqueFieldId: null,
  version: 0,
};

const determineNodeType = (node: TreeOCRField): FieldNodeType => {
  if (node.nodeType) return node.nodeType;
  if (node.tableName || node.parentTableId) return "table";
  if (node.children && node.children.length > 0) {
    return node.tableName ? "table" : "section";
  }
  return "field";
};

const normalizeFields = (
  fields: TreeOCRField[],
): {
  rootIds: string[];
  nodesById: Record<string, FieldNode>;
  uniqueFieldId: string | null;
} => {
  const nodesById: Record<string, FieldNode> = {};
  const rootIds: string[] = [];
  let uniqueFieldId: string | null = null;

  const walk = (list: TreeOCRField[], parentId?: string) => {
    list.forEach((node) => {
      const nodeType = determineNodeType(node);
      const fallbackLabel =
        node.fieldName ??
        node.suggestedLabel ??
        node.rawFieldName ??
        node.tableName ??
        "";
      const normalized: FieldNode = {
        id: node.id,
        parentId,
        nodeType,
        label: fallbackLabel,
        suggestedLabel:
          node.suggestedLabel ?? node.fieldName ?? node.rawFieldName ?? "",
        rawFieldName: node.rawFieldName ?? node.fieldName ?? fallbackLabel,
        sampleValue: node.sampleValue,
        subtitle: node.subtitle,
        confidence: node.confidence,
        tableName: node.tableName,
        isSelected: Boolean(node.isSelected),
        isUniqueIdentifier: Boolean(node.isUniqueIdentifier),
        childIds: [],
        isUserAdded: node.isUserAdded,
        metaData: node.metaData,
        apiResponseKey: node.apiResponseKey,
      };

      nodesById[node.id] = normalized;
      if (!parentId) {
        rootIds.push(node.id);
      } else {
        const parentNode = nodesById[parentId];
        if (parentNode) {
          parentNode.childIds.push(node.id);
        } else {
          rootIds.push(node.id);
        }
      }

      if (node.isUniqueIdentifier) {
        uniqueFieldId = node.id;
      }

      if (node.children?.length) {
        walk(node.children, node.id);
      }
    });
  };

  walk(fields);
  return { rootIds, nodesById, uniqueFieldId };
};

const insertIdAfter = (list: string[], newId: string, afterId?: string) => {
  if (!afterId) {
    list.push(newId);
    return;
  }
  const targetIndex = list.findIndex((id) => id === afterId);
  if (targetIndex === -1) {
    list.push(newId);
    return;
  }
  list.splice(targetIndex + 1, 0, newId);
};

const setSelectionForNode = (
  state: FieldMappingState,
  nodeId: string,
  selected: boolean,
) => {
  const node = state.nodesById[nodeId];
  if (!node) return;

  node.isSelected = selected;
  if (!selected && node.isUniqueIdentifier) {
    node.isUniqueIdentifier = false;
    if (state.uniqueFieldId === nodeId) {
      state.uniqueFieldId = null;
    }
  }

  if (node.childIds?.length) {
    node.childIds.forEach((childId) =>
      setSelectionForNode(state, childId, selected),
    );
  }
};

const syncParentSelection = (state: FieldMappingState, parentId?: string) => {
  if (!parentId) return;
  const parent = state.nodesById[parentId];
  if (!parent || !parent.childIds || parent.childIds.length === 0) return;

  const allSelected = parent.childIds.every((childId) => {
    const child = state.nodesById[childId];
    return child ? child.isSelected : false;
  });

  parent.isSelected = allSelected;
  syncParentSelection(state, parent.parentId);
};

const removeNodeRecursive = (state: FieldMappingState, nodeId: string) => {
  const node = state.nodesById[nodeId];
  if (!node) return;
  if (node.childIds?.length) {
    [...node.childIds].forEach((childId) =>
      removeNodeRecursive(state, childId),
    );
  }
  if (state.uniqueFieldId === nodeId) {
    state.uniqueFieldId = null;
  }
  delete state.nodesById[nodeId];
};

const fieldMappingSlice = createSlice({
  name: "fieldMapping",
  initialState,
  reducers: {
    replaceTree: (state, action: PayloadAction<TreeOCRField[] | undefined>) => {
      const normalized = normalizeFields(action.payload ?? []);
      state.rootIds = normalized.rootIds;
      state.nodesById = normalized.nodesById;
      state.uniqueFieldId = normalized.uniqueFieldId;
      state.version += 1;
    },
    updateNodeLabel: (
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) => {
      const node = state.nodesById[action.payload.id];
      if (!node) return;

      // Save the old label to check if apiResponseKey was previously synced
      const oldLabel = node.label;

      node.label = action.payload.label;
      if (node.isUserAdded) {
        node.rawFieldName = action.payload.label;
        // Auto-update apiResponseKey if not manually edited (sync with old label)
        // If apiResponseKey equals the old label, it means they were in sync before,
        // so we should update it to match the new label
        if (!node.apiResponseKey || node.apiResponseKey === oldLabel) {
          node.apiResponseKey = action.payload.label;
        }
      }
      state.version += 1;
    },
    updateNodeApiResponseKey: (
      state,
      action: PayloadAction<{ id: string; apiResponseKey: string }>,
    ) => {
      const node = state.nodesById[action.payload.id];
      if (!node) return;
      node.apiResponseKey = action.payload.apiResponseKey;
      state.version += 1;
    },
    toggleNodeSelection: (state, action: PayloadAction<{ id: string }>) => {
      const node = state.nodesById[action.payload.id];
      if (!node) return;
      const nextSelected = !node.isSelected;
      setSelectionForNode(state, action.payload.id, nextSelected);
      syncParentSelection(state, node.parentId);
      state.version += 1;
    },
    setUniqueIdentifier: (state, action: PayloadAction<{ id: string }>) => {
      const node = state.nodesById[action.payload.id];
      if (!node) return;

      if (state.uniqueFieldId === action.payload.id) {
        node.isUniqueIdentifier = false;
        state.uniqueFieldId = null;
      } else {
        if (state.uniqueFieldId) {
          const previous = state.nodesById[state.uniqueFieldId];
          if (previous) {
            previous.isUniqueIdentifier = false;
          }
        }
        node.isUniqueIdentifier = true;
        if (!node.isSelected) {
          node.isSelected = true;
          syncParentSelection(state, node.parentId);
        }
        state.uniqueFieldId = node.id;
      }
      state.version += 1;
    },
    clearUniqueIdentifier: (state) => {
      if (state.uniqueFieldId) {
        const node = state.nodesById[state.uniqueFieldId];
        if (node) {
          node.isUniqueIdentifier = false;
        }
      }
      state.uniqueFieldId = null;
      state.version += 1;
    },
    addFieldNode: (
      state,
      action: PayloadAction<{
        id: string;
        parentId?: string;
        insertAfterId?: string;
        label?: string;
      }>,
    ) => {
      const {
        id,
        parentId,
        insertAfterId,
        label = "New Field",
      } = action.payload;

      const newNode: FieldNode = {
        id,
        parentId,
        nodeType: "field",
        label,
        suggestedLabel: label,
        rawFieldName: label,
        sampleValue: "",
        isSelected: true,
        isUniqueIdentifier: false,
        childIds: [],
        isUserAdded: true,
        apiResponseKey: label, // Initialize with the label value
      };

      state.nodesById[id] = newNode;

      if (parentId) {
        const parent = state.nodesById[parentId];
        if (!parent) {
          state.version += 1;
          return;
        }
        const siblings = parent.childIds;
        if (insertAfterId) {
          insertIdAfter(siblings, id, insertAfterId);
        } else {
          siblings.push(id);
        }
        syncParentSelection(state, parentId);
      } else {
        insertAfterId
          ? insertIdAfter(state.rootIds, id, insertAfterId)
          : state.rootIds.push(id);
      }
      state.version += 1;
    },
    addSectionNode: (
      state,
      action: PayloadAction<{ id: string; afterSectionId?: string }>,
    ) => {
      const { id, afterSectionId } = action.payload;
      const newSection: FieldNode = {
        id,
        nodeType: "section",
        label: "",
        isSelected: false,
        childIds: [],
        isUserAdded: true,
      };

      let targetList: string[] = state.rootIds;
      let parentId: string | undefined;

      if (afterSectionId) {
        const targetNode = state.nodesById[afterSectionId];
        if (targetNode) {
          parentId = targetNode.parentId;
          const parentNode = parentId ? state.nodesById[parentId] : undefined;
          targetList = parentNode ? parentNode.childIds : state.rootIds;
        }
      }

      newSection.parentId = parentId;
      state.nodesById[id] = newSection;
      insertIdAfter(targetList, id, afterSectionId);
      state.version += 1;
    },
    addTableNode: (
      state,
      action: PayloadAction<{
        id: string;
        parentId: string;
        insertAfterId?: string;
        label?: string;
      }>,
    ) => {
      const {
        id,
        parentId,
        insertAfterId,
        label = "New Table",
      } = action.payload;
      const parent = state.nodesById[parentId];
      if (!parent) {
        state.version += 1;
        return;
      }
      const newTable: FieldNode = {
        id,
        parentId,
        nodeType: "table",
        label,
        suggestedLabel: label,
        rawFieldName: label,
        tableName: label,
        sampleValue: "",
        isSelected: true,
        childIds: [],
        isUserAdded: true,
      };
      state.nodesById[id] = newTable;
      const siblings = parent.childIds;
      if (insertAfterId) {
        insertIdAfter(siblings, id, insertAfterId);
      } else {
        siblings.push(id);
      }
      state.version += 1;
    },
    removeNode: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const node = state.nodesById[id];
      if (!node) return;

      const parentId = node.parentId;
      removeNodeRecursive(state, id);

      if (parentId) {
        const parent = state.nodesById[parentId];
        if (parent?.childIds) {
          parent.childIds = parent.childIds.filter((childId) => childId !== id);
        }
        syncParentSelection(state, parentId);
      } else {
        state.rootIds = state.rootIds.filter((rootId) => rootId !== id);
      }

      state.version += 1;
    },
    selectAllNodes: (state) => {
      Object.values(state.nodesById).forEach((node) => {
        node.isSelected = true;
      });
      state.version += 1;
    },
    deselectAllNodes: (state) => {
      Object.values(state.nodesById).forEach((node) => {
        node.isSelected = false;
        node.isUniqueIdentifier = false;
      });
      state.uniqueFieldId = null;
      state.version += 1;
    },
  },
});

export const {
  replaceTree,
  updateNodeLabel,
  updateNodeApiResponseKey,
  toggleNodeSelection,
  setUniqueIdentifier,
  clearUniqueIdentifier,
  addFieldNode,
  addSectionNode,
  addTableNode,
  removeNode,
  selectAllNodes,
  deselectAllNodes,
} = fieldMappingSlice.actions;

export const selectFieldMappingState = (state: RootState) => state.fieldMapping;

export const selectRootIds = (state: RootState) => state.fieldMapping.rootIds;

export const selectNodesById = (state: RootState) =>
  state.fieldMapping.nodesById;

export const selectFieldVersion = (state: RootState) =>
  state.fieldMapping.version;

export const selectUniqueFieldId = (state: RootState) =>
  state.fieldMapping.uniqueFieldId;

export const makeSelectFieldNode = () =>
  createSelector(
    [
      (state: RootState) => state.fieldMapping.nodesById,
      (_: RootState, id: string) => id,
    ],
    (nodesById, id) => nodesById[id],
  );

export const denormalizeFieldTree = (
  state: FieldMappingState,
): TreeOCRField[] => {
  const buildNode = (id: string): TreeOCRField | null => {
    const node = state.nodesById[id];
    if (!node) return null;

    const children =
      node.childIds.length > 0
        ? (node.childIds.map(buildNode).filter(Boolean) as TreeOCRField[])
        : undefined;

    return {
      id: node.id,
      suggestedLabel: node.suggestedLabel ?? node.label,
      sampleValue: node.sampleValue ?? "",
      isSelected: node.isSelected,
      fieldName: node.label,
      rawFieldName: node.rawFieldName,
      confidence: node.confidence,
      subtitle: node.subtitle,
      metaData: node.metaData,
      tableName: node.tableName,
      isUniqueIdentifier: node.isUniqueIdentifier,
      isUserAdded: node.isUserAdded,
      nodeType: node.nodeType,
      parentId: node.parentId,
      apiResponseKey: node.apiResponseKey,
      children,
    };
  };

  return state.rootIds
    .map(buildNode)
    .filter((node): node is TreeOCRField => Boolean(node));
};

export default fieldMappingSlice.reducer;
